<template>
  <div class="group p-2 relative flex flex-col justify-center items-center">
    <!-- MobileMediaDisplay (screen size MD or below) -->
    <template v-if="isMobile">
      <div class="flex flex-col items-center w-full">
        <button
          v-if="isImage"
          type="button"
          class="w-full flex justify-center"
          :aria-label="$t('MediaDisplay.actions.viewFullImage')"
          @click="showFullImage = true"
        >
          <AppSpinner v-if="isHeicImage && heicJpgBlob === false" />

          <img
            v-else-if="heicJpgBlob"
            :src="heicJpgBlobUrl"
            :alt="media.name"
            class="max-h-32 w-auto object-contain rounded-md"
            @error="$emit('refresh', media)"
          >

          <img
            v-else
            :src="media.custom_properties.access_url"
            :alt="media.name"
            class="max-h-32 w-auto object-contain rounded-md"
            @error="$emit('refresh', media)"
          >
        </button>

        <div
          v-else
          class="flex flex-col items-center w-full"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-7 h-7">
    <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
  </svg>

          <button
            v-if="isPdf"
            type="button"
            class="font-semibold mt-2 text-center break-all"
            :aria-label="$t('MediaDisplay.actions.viewFullPdf')"
            @click="showFullPdf = true"
          >
            {{ media.file_name }}
          </button>

          <p
            v-else
            class="font-semibold mt-2 text-center break-all"
          >
            {{ media.file_name }}
          </p>
        </div>


        <div class="mt-3 flex items-center justify-center space-x-3 w-full">
          <button
            v-if="allowDelete"
            type="button"
            class="btn-icon btn-icon-red btn-icon--bg-red"
            :aria-label="$t('MediaDisplay.actions.deleteMedia')"
            @click="deleteMedia"
          >
            <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-5 h-5">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
          </button>

          <a
            class="btn-icon btn-icon-blue btn-icon--bg-blue"
            :href="downloadMediaRoute"
            :aria-label="$t('MediaDisplay.actions.downloadMedia')"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
    <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
</svg>
          </a>
        </div>

        <slot />
      </div>
    </template>

    <!-- DesktopMediaDisplay (screen size above MD) -->
    <template v-else>
      <button
        v-if="media.mime_type.startsWith('image/')"
        type="button"
        :aria-label="$t('MediaDisplay.actions.viewFullImage')"
        @click="showFullImage = true"
      >
        <AppSpinner v-if="media.mime_type.startsWith('image/hei') && heicJpgBlob === false" />

        <img
          v-else-if="heicJpgBlob"
          :src="heicJpgBlobUrl"
          :alt="media.name"
          @error="$emit('refresh', media)"
        >

        <img
          v-else
          :src="media.custom_properties.access_url"
          :alt="media.name"
          @error="$emit('refresh', media)"
        >
      </button>

      <div
        v-else
        class="flex flex-col items-center w-full"
      >
        <div class="space-x-2 items-center hidden group-hover:flex">
          <button
            v-if="allowDelete"
            type="button"
            class="btn-icon btn-icon-red btn-icon--bg-red"
            :aria-label="$t('MediaDisplay.actions.deleteMedia')"
            @click="deleteMedia"
          >
            <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-5 h-5">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
          </button>

          <a
            class="btn-icon btn-icon-blue btn-icon--bg-blue"
            :href="downloadMediaRoute"
            :aria-label="$t('MediaDisplay.actions.downloadMedia')"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
    <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
</svg>
          </a>
        </div>

        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-7 h-7 group-hover:hidden">
    <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
  </svg>

        <button
          v-if="isPdf"
          type="button"
          class="btn btn--secondary-blue btn--small mt-2 hidden group-hover:block"
          :aria-label="$t('MediaDisplay.actions.viewFullPdf')"
          @click="showFullPdf = true"
        >
          <span
            class="font-semibold text-nowrap"
          >
            View {{ media.file_name }}
          </span>
        </button>

        <p
          class="font-semibold mt-2 text-nowrap pt-1 pb-0.5"
          :class="{ 'group-hover:hidden': isPdf }"
        >
          {{ media.file_name }}
        </p>
      </div>

      <slot />

      <div
        v-if="media.mime_type.startsWith('image/')"
        class="absolute hidden group-hover:flex space-x-2 items-center"
      >
        <button
          v-if="allowDelete"
          type="button"
          class="btn-icon btn-icon-red btn-icon--bg-red"
          :aria-label="$t('MediaDisplay.actions.deleteMedia')"
          @click="deleteMedia"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-5 h-5">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
        </button>

        <a
          class="btn-icon btn-icon-blue btn-icon--bg-blue"
          :href="downloadMediaRoute"
          :aria-label="$t('MediaDisplay.actions.downloadMedia')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
    <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
</svg>
        </a>
      </div>
    </template>

    <Teleport
      v-if="showFullImage"
      to="#modals"
    >
      <div
        class="absolute"
        tabindex="0"
      >
        <div
          class="fixed z-[60] flex justify-center items-center m-4 inset-0"
          @mousedown="onBackdropMouseDown"
          @mouseup="onBackdropMouseUp"
        >
          <img
            :src="media.custom_properties.access_url"
            :alt="media.name"
            class="max-h-screen p-10"
            @error="$emit('refresh', media)"
          >
        </div>

        <button
          type="button"
          :aria-label="$t('MediaDisplay.actions.closeImage')"
          @click="showFullImage = false"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="h-10 w-10 text-white fixed z-[60] top-0 right-0 m-5">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M6 18L18 6M6 6l12 12"
    />
</svg>
        </button>

        <div
          class="opacity-75 fixed inset-0 z-50 bg-black"
        />
      </div>
    </Teleport>

    <Teleport
      v-if="showFullPdf"
      to="#modals"
    >
      <div
        class="absolute"
        tabindex="0"
      >
        <div
          class="fixed z-[60] flex justify-center items-center m-4 inset-0"
          @mousedown="onBackdropMouseDown"
          @mouseup="onBackdropMouseUp"
        >
          <iframe
            :src="media.custom_properties.access_url"
            :title="media.name"
            class="w-full h-full max-w-5xl bg-white rounded"
          />
        </div>

        <button
          type="button"
          :aria-label="$t('MediaDisplay.actions.closePdf')"
          @click="showFullPdf = false"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="h-10 w-10 text-white fixed z-[60] top-0 right-0 m-5">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M6 18L18 6M6 6l12 12"
    />
</svg>
        </button>

        <div
          class="opacity-75 fixed inset-0 z-50 bg-black"
        />
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { Media } from '@/composables/useMediaCollection';
import useScreenSize, { ScreenSize } from '@/composables/useScreenSize';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { convertHeicUrlToJpgBlob } from '@/helpers';
import { t } from '@/i18n';
import { useMediaStore } from '@/store/media';
import { route } from '@/ziggy-shim';
import { onKeyStroke } from '@vueuse/core';
import { heicTo, isHeic } from 'heic-to';
import Swal from 'sweetalert2';
import { computed, onUnmounted, ref, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    media: Media;
    allowDelete?: boolean;
    deleteAction?: (uuid: string) => Promise<void>;
  }>(),
  {
    allowDelete: true,
    deleteAction: undefined,
  },
);

const emits = defineEmits<{
  (e: 'delete', media: Media);
  (e: 'refresh', media: Media);
}>();

const mediaStore = useMediaStore();
const deleteMediaAction = useStoreApiAction(mediaStore.deleteMedia);
const { screenSize } = useScreenSize();

const isMobile = computed(() => {
  if(!screenSize.value) {
    return false;
  }

  return screenSize.value <= ScreenSize.MD;
});

const isImage = computed(() => props.media.mime_type.startsWith('image/'));
const isHeicImage = computed(() => props.media.mime_type.startsWith('image/hei'));
const isPdf = computed(() => props.media.mime_type === 'application/pdf');

const deleteMedia = () => {
  Swal.fire({
    icon: 'question',
    title: t('MediaDisplay.deleteTitle'),
    text: t('MediaDisplay.deleteConfirmation'),
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      if(props.deleteAction) {
        props.deleteAction(props.media.uuid).then(() => {
          emits('delete', props.media);
        }).catch(console.error);
      } else {
        deleteMediaAction.request(props.media.uuid).then(() => {
          emits('delete', props.media);
        }).catch(console.error);
      }
    }
  });
};

const downloadMediaRoute = computed(() => {
  return route('api.v1.media.download', props.media.uuid);
});

const showFullImage = ref(false);
const showFullPdf = ref(false);
const heicJpgBlob = ref<Blob | undefined | false>(false);
const backdropMouseDown = ref(false);

const onBackdropMouseDown = (event: MouseEvent) => {
  backdropMouseDown.value = event.target === event.currentTarget;
};

const onBackdropMouseUp = (event: MouseEvent) => {
  if(backdropMouseDown.value && event.target === event.currentTarget) {
    showFullImage.value = false;
    showFullPdf.value = false;
  }

  backdropMouseDown.value = false;
};

onKeyStroke('Escape', () => {
  showFullImage.value = false;
  showFullPdf.value = false;
});

watch(props.media, async () => {
  if(!isHeicImage.value) {
    return;
  }

  heicJpgBlob.value = await convertHeicUrlToJpgBlob(props.media.custom_properties.access_url, props.media.file_name);
}, { immediate: true });

const heicJpgBlobUrl = computed(() => {
  if(heicJpgBlob.value) {
    return URL.createObjectURL(heicJpgBlob.value);
  }

  return undefined;
});

onUnmounted(() => {
  if(heicJpgBlobUrl.value) {
    URL.revokeObjectURL(heicJpgBlobUrl.value);
  }
});
</script>

<style scoped></style>
